import { useEffect, useMemo, useState } from 'react';

const DEFAULT_EVENTS_API_URL = 'https://sistema.mevambrasilia.org/evento/feed';
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

const fallbackEventTitle = 'Evento MEVAM Brasilia';

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatCurrency = (value) => {
  const numericValue = parseNumericValue(value);

  if (numericValue === null) {
    return null;
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numericValue);
};

const resolveAbsoluteUrl = (value, origin) => {
  if (!value) {
    return '';
  }

  try {
    return new URL(value, origin).toString();
  } catch {
    return value;
  }
};

const buildFeedUrl = (baseUrl, limit) => {
  try {
    const url = new URL(baseUrl);

    if (limit) {
      url.searchParams.set('limit', String(limit));
    }

    return url.toString();
  } catch {
    if (!limit) {
      return baseUrl;
    }

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}limit=${limit}`;
  }
};

const readCachedEvents = (cacheKey) => {
  try {
    const rawValue = localStorage.getItem(cacheKey);

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);

    if (!Array.isArray(parsedValue.events) || typeof parsedValue.timestamp !== 'number') {
      return null;
    }

    return parsedValue;
  } catch {
    return null;
  }
};

const writeCachedEvents = (cacheKey, events) => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify({
      events,
      timestamp: Date.now()
    }));
  } catch {
    return;
  }
};

const buildFriendlyErrorMessage = ({ status, contentType, feedUrl, hasCache }) => {
  if (status === 404) {
    return hasCache
      ? `O endpoint de eventos ${feedUrl} respondeu 404. Exibindo a ultima sincronizacao disponivel.`
      : `O endpoint de eventos ${feedUrl} respondeu 404 no servidor publicado.`;
  }

  if (contentType && !contentType.includes('application/json')) {
    return hasCache
      ? `O endpoint ${feedUrl} nao retornou JSON valido. Exibindo a ultima sincronizacao disponivel.`
      : `O endpoint ${feedUrl} nao retornou JSON valido para o site.`;
  }

  return hasCache
    ? `Nao foi possivel atualizar os eventos agora a partir de ${feedUrl}. Exibindo a ultima sincronizacao disponivel.`
    : `Nao foi possivel carregar os eventos publicados no sistema a partir de ${feedUrl}.`;
};

const getEventOrigin = (feedUrl) => {
  try {
    return new URL(feedUrl).origin;
  } catch {
    return 'https://sistema.mevambrasilia.org';
  }
};

const normalizeEvent = (event, index, origin) => {
  const startDate = event.startDate || event.data_inicio || event.dataInicio || event.date || null;
  const endDate = event.endDate || event.data_fim || event.dataFim || null;
  const imageUrl = resolveAbsoluteUrl(event.imageUrl || event.imagem_url || event.imagem || '', origin);
  const eventUrl = resolveAbsoluteUrl(
    event.registrationUrl ||
      event.linkInscricao ||
      event.url ||
      (event.slug ? `/evento/${event.slug}` : '/evento'),
    origin
  );

  return {
    id: event.id || event.slug || `evento-${index}`,
    title: event.title || event.titulo || event.nome || fallbackEventTitle,
    subtitle:
      event.subtitle ||
      event.subtitulo ||
      event.statusLabel ||
      (event.inscricoes_abertas === false
        ? 'Inscricoes encerradas'
        : event.gratuito
          ? 'Evento gratuito'
          : formatCurrency(event.valor_inscricao) || 'Inscricoes disponiveis'),
    description:
      event.resumo ||
      event.description ||
      event.descricao ||
      'Confira os detalhes do evento e faca sua inscricao.',
    startDate,
    endDate,
    location: event.location || event.local || 'MEVAM Brasilia',
    address: event.address || event.endereco_completo || event.endereco || 'Brasilia - DF',
    status:
      event.status ||
      event.situacao ||
      (event.inscricoes_abertas === false ? 'Inscricoes encerradas' : 'Inscricoes abertas'),
    capacity:
      event.capacity ||
      event.capacidade ||
      (event.gratuito ? 'Entrada gratuita' : formatCurrency(event.valor_inscricao)) ||
      'Vagas limitadas',
    featured: Boolean(event.featured || event.destaque || index === 0),
    imageUrl,
    eventUrl,
    isFree: Boolean(event.gratuito),
    registrationOpen: event.inscricoes_abertas !== false,
    category: event.categoria || '',
    raw: event
  };
};

export const formatPublicEventDate = (value) => {
  if (!value) {
    return 'Data a confirmar';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Data a confirmar';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

export const formatPublicEventTime = (value) => {
  if (!value) {
    return 'Horario a confirmar';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Horario a confirmar';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const usePublicEvents = ({ limit } = {}) => {
  const configuredApiUrl = process.env.REACT_APP_EVENTS_API_URL || DEFAULT_EVENTS_API_URL;
  const cacheTtlMs = parseNumericValue(process.env.REACT_APP_EVENTS_CACHE_TTL_MS) || DEFAULT_CACHE_TTL_MS;

  const feedUrl = useMemo(
    () => buildFeedUrl(configuredApiUrl, limit),
    [configuredApiUrl, limit]
  );

  const cacheKey = useMemo(() => `mevam_public_events:${feedUrl}`, [feedUrl]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const cachedValue = readCachedEvents(cacheKey);
    const cacheIsFresh = cachedValue && Date.now() - cachedValue.timestamp < cacheTtlMs;

    if (cacheIsFresh) {
      setEvents(cachedValue.events);
      setLastUpdated(cachedValue.timestamp);
      setLoading(false);
      setError('');
      return () => {
        isMounted = false;
      };
    }

    const loadEvents = async () => {
      try {
        const response = await fetch(feedUrl, {
          headers: {
            Accept: 'application/json'
          }
        });

        const contentType = response.headers.get('content-type') || '';

        if (!response.ok) {
          const error = new Error('Falha ao carregar eventos publicos');
          error.status = response.status;
          error.contentType = contentType;
          throw error;
        }

        if (!contentType.includes('application/json')) {
          const error = new Error('Formato invalido de resposta');
          error.status = response.status;
          error.contentType = contentType;
          throw error;
        }

        const payload = await response.json();
        const incomingEvents = Array.isArray(payload)
          ? payload
          : payload.data || payload.events || [];

        if (!Array.isArray(incomingEvents)) {
          throw new Error('Formato invalido do feed de eventos');
        }

        const normalizedEvents = incomingEvents.map((event, index) => normalizeEvent(event, index, getEventOrigin(feedUrl)));

        writeCachedEvents(cacheKey, normalizedEvents);

        if (isMounted) {
          setEvents(normalizedEvents);
          setLastUpdated(Date.now());
          setError('');
        }
      } catch (requestError) {
        if (isMounted) {
          const friendlyErrorMessage = buildFriendlyErrorMessage({
            status: requestError?.status,
            contentType: requestError?.contentType,
            feedUrl,
            hasCache: Boolean(cachedValue)
          });

          if (cachedValue) {
            setEvents(cachedValue.events);
            setLastUpdated(cachedValue.timestamp);
            setError(friendlyErrorMessage);
          } else {
            setEvents([]);
            setError(friendlyErrorMessage);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, [cacheKey, cacheTtlMs, feedUrl]);

  return {
    events,
    loading,
    error,
    lastUpdated,
    apiUrl: feedUrl
  };
};

export default usePublicEvents;